/**
 * Collapsible: one disclosure region at Radix parity.
 *
 * The trigger carries `aria-expanded` and `aria-controls`; the content is
 * `hidden` while closed, but only after an exit animation finishes. On every
 * open and close the content's size is measured and published as
 * `--radix-collapsible-content-height` / `-width`, which the shadcn
 * `collapsible-down` / `collapsible-up` keyframes animate between.
 *
 * `attachCollapsible` is idempotent per root: attaching a root that already
 * has a live controller returns that controller (the sidebar attaches its menu
 * groups this way without double-binding a component wrapper's attach).
 *
 * @markup collapsible div [ui-collapsible] data-state=open|closed data-disabled?
 * @markup collapsible-trigger button [ui-collapsible-trigger] (aria-expanded, aria-controls, data-state, data-disabled added)
 * @markup collapsible-content div [ui-collapsible-content] hidden (data-state=open|closed, id, --radix-collapsible-content-height|width added)
 */
import { generateId } from 'hydrogen/id';
import { createControllable, isFlaggedDisabled, syncContent, applyDisabled, } from './_layout-state.js';
import { partOf, partsOf, partTarget, setState } from './_dom.js';
const attached = new WeakMap();
/** The live controller attached to `root`, if any. */
export function collapsibleOf(root) {
    return attached.get(root);
}
export function attachCollapsible(root, initial = {}) {
    const existing = attached.get(root);
    if (existing)
        return existing;
    let options = { ...initial };
    let destroyed = false;
    let first = true;
    const state = createControllable(options.open ?? options.defaultOpen ?? root.getAttribute('data-state') === 'open', (next) => options.onOpenChange?.(next));
    if (options.open !== undefined)
        state.control(options.open);
    const triggers = () => partsOf(root, 'collapsible', 'collapsible-trigger');
    const content = () => partOf(root, 'collapsible', 'collapsible-content');
    const isDisabled = () => options.disabled ?? isFlaggedDisabled(root);
    function sync() {
        const open = state.get();
        const disabled = isDisabled();
        const region = content();
        if (region)
            region.id ||= generateId();
        setState(open ? 'open' : 'closed', root);
        applyDisabled(root, disabled);
        for (const part of triggers()) {
            const trigger = partTarget(part);
            trigger.setAttribute('aria-expanded', String(open));
            if (region)
                trigger.setAttribute('aria-controls', region.id);
            if (trigger instanceof HTMLButtonElement && !trigger.hasAttribute('type'))
                trigger.type = 'button';
            setState(open ? 'open' : 'closed', part, trigger);
            applyDisabled(trigger, disabled);
        }
        if (region) {
            applyDisabled(region, disabled);
            syncContent(region, open, { prefixes: ['collapsible'], initial: first });
        }
        first = false;
    }
    function request(open) {
        if (destroyed || isDisabled())
            return;
        state.request(open);
        sync();
    }
    function onClick(event) {
        const target = event.target;
        if (!(target instanceof Element))
            return;
        const part = target.closest('[data-slot="collapsible-trigger"]');
        if (!part || part.closest('[data-slot="collapsible"]') !== root)
            return;
        if (isFlaggedDisabled(partTarget(part)))
            return;
        request(!state.get());
    }
    function onKeyDown(event) {
        // Non-button triggers (asChild onto an `<a>`/`<div>`) need Enter/Space themselves.
        const target = event.target;
        if (!(target instanceof HTMLElement) || target instanceof HTMLButtonElement)
            return;
        if (event.key !== 'Enter' && event.key !== ' ')
            return;
        const part = target.closest('[data-slot="collapsible-trigger"]');
        if (!part || part.closest('[data-slot="collapsible"]') !== root || target.tagName === 'A')
            return;
        event.preventDefault();
        request(!state.get());
    }
    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeyDown);
    sync();
    const controller = {
        open: () => request(true),
        close: () => request(false),
        toggle: () => request(!state.get()),
        get isOpen() {
            return state.get();
        },
        update(next) {
            if (destroyed)
                return;
            options = { ...options, ...next };
            if ('open' in next)
                state.control(next.open);
            sync();
        },
        destroy() {
            if (destroyed)
                return;
            destroyed = true;
            attached.delete(root);
            root.removeEventListener('click', onClick);
            root.removeEventListener('keydown', onKeyDown);
        },
    };
    attached.set(root, controller);
    return controller;
}
